/**
 * Per-section diff card for the HITL review mode (Phase 32.2).
 *
 * Renders a card with:
 *   - Header: section heading + status badge
 *   - Body: side-by-side diff (canonical vs proposed)
 *   - Footer: Accept | Reject | Regenerate with feedback
 *
 * The diff viewer uses react-diff-viewer-continued for green/red highlights.
 */

import { useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";

const STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
};

const STATUS_CLASSES = {
  pending: "review-badge--pending",
  accepted: "review-badge--accepted",
  rejected: "review-badge--rejected",
};

export default function SectionDiff({
  section,
  canonicalText,
  proposedText,
  headingChanged,
  onAccept,
  onReject,
  busy,
}) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resolved = section.status === "accepted" || section.status === "rejected";

  const handleReject = async () => {
    setFeedbackOpen(true);
  };

  const handleSubmitReject = async () => {
    setSubmitting(true);
    try {
      await onReject(section.sectionId, feedback.trim() || undefined);
      setFeedbackOpen(false);
      setFeedback("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`review-section-card ${resolved ? "review-section-card--resolved" : ""}`}>
      <div className="review-section-header">
        <h3 className="review-section-title">
          {section.sectionId}
          {headingChanged && (
            <span
              className="review-heading-warning"
              title="Heading changed unexpectedly — section cannot be accepted. Reject and regenerate."
            >
              Warning: heading changed
            </span>
          )}
        </h3>
        <span className={`review-badge ${STATUS_CLASSES[section.status] || ""}`}>
          {STATUS_LABELS[section.status] || section.status}
        </span>
      </div>

      <div className="review-section-diff">
        <ReactDiffViewer
          oldValue={canonicalText || ""}
          newValue={proposedText || ""}
          splitView={true}
          compareMethod={DiffMethod.WORDS}
          hideLineNumbers={true}
          useDarkTheme={document.documentElement.getAttribute("data-theme") === "dark"}
          styles={{
            contentText: { fontSize: "13px", lineHeight: "1.6", fontFamily: "inherit" },
          }}
        />
      </div>

      {!resolved && (
        <div className="review-section-actions">
          {feedbackOpen ? (
            <div className="review-feedback-form">
              <textarea
                className="review-feedback-input"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Feedback for regeneration (optional)"
                rows={3}
                maxLength={2000}
              />
              <div className="review-feedback-buttons">
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={handleSubmitReject}
                  disabled={submitting || busy}
                >
                  {feedback.trim() ? "Reject & Regenerate" : "Reject"}
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => { setFeedbackOpen(false); setFeedback(""); }}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="btn-primary btn-sm review-btn-accept"
                onClick={() => onAccept(section.sectionId)}
                disabled={busy || headingChanged}
                title={headingChanged ? "Cannot accept — heading changed" : "Accept this section"}
              >
                Accept
              </button>
              <button
                type="button"
                className="btn-secondary btn-sm review-btn-reject"
                onClick={handleReject}
                disabled={busy}
              >
                Reject
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
